import { useEffect } from 'react';

export default function ConfirmationModal({
    isOpen,
    onClose,
    onConfirm,
    title,
    message,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    isDanger = false
}) {
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    // Detect RTL from document direction
    const isRTL = document.documentElement.dir === 'rtl';

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            {/* Backdrop */}
            <div
                className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0"
                onClick={(e) => {
                    // Only close if clicking the backdrop/wrapper, not the modal content
                    if (e.target === e.currentTarget) {
                        onClose();
                    }
                }}
            >
                <div
                    className="fixed inset-0 bg-black/80 backdrop-blur-sm transition-opacity"
                    aria-hidden="true"
                ></div>

                {/* Center modal */}
                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                {/* Modal panel */}
                <div
                    className="inline-block align-bottom bg-bg-card border border-gold/20 rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6 relative z-10"
                >
                    <div className={`sm:flex sm:items-start ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
                        {/* Icon */}
                        <div className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full ${isDanger ? 'bg-danger-100' : 'bg-info-100'} ${isRTL ? 'sm:ml-0 sm:mr-4' : 'sm:mx-0 sm:mr-4'} sm:h-10 sm:w-10`}>
                            {isDanger ? (
                                <svg className="h-6 w-6 text-danger-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            ) : (
                                <svg className="h-6 w-6 text-info-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            )}
                        </div>

                        {/* Content */}
                        <div className={`mt-3 text-center sm:mt-0 ${isRTL ? 'sm:mr-4 sm:text-right' : 'sm:ml-4 sm:text-left'} flex-1`}>
                            <h3 className="text-lg leading-6 font-medium text-text" id="modal-title">
                                {title}
                            </h3>
                            <div className="mt-2">
                                <p className="text-sm text-text-muted">
                                    {message}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className={`mt-5 sm:mt-4 sm:flex ${isRTL ? 'sm:flex-row' : 'sm:flex-row-reverse'} gap-3`}>
                        <button
                            type="button"
                            className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 ${isRTL ? 'sm:mr-3' : 'sm:ml-3'} sm:w-auto sm:text-sm transition-colors ${isDanger
                                ? 'bg-danger-600 hover:bg-danger-700 focus:ring-danger-500'
                                : 'bg-gold-gradient hover:opacity-90 focus:ring-gold'
                                }`}
                            onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                onConfirm();
                            }}
                        >
                            {confirmText}
                        </button>
                        <button
                            type="button"
                            className="mt-3 w-full inline-flex justify-center rounded-md border border-gold/20 shadow-sm px-4 py-2 bg-bg-soft text-base font-medium text-text-muted hover:bg-gold/10 hover:text-gold focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold sm:mt-0 sm:w-auto sm:text-sm transition-colors"
                            onClick={(e) => {
                                e.stopPropagation();
                                onClose();
                            }}
                        >
                            {cancelText}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
